import PropTypes from "prop-types";

const reasonLabels = {
    binance_enabled_false: "Binance disabled for configured coin",
    missing_binance_keys: "Binance API keys missing",
    no_coins_enabled_for_binance: "No coins enabled for Binance",
    live_flags_disabled: "Live trading flags are disabled",
    live_wallet_disabled: "Live wallet disabled",
    runtime_mode_conflict: "Runtime mode conflict",
    readiness_payload_missing: "Live status checks unavailable",
    readiness_payload_malformed: "Live status checks malformed",
    readiness_payload_incomplete: "Live status check fields missing",
    readiness_alias_conflict: "Live status check aliases conflict",
    readiness_payload_stale: "Live status checks stale",
};

const READINESS_MAX_AGE_MS = 5 * 60 * 1000;
const READINESS_FUTURE_SKEW_MS = 60 * 1000;

const envBadges = {
    paper: "bg-gray-800 text-gray-200",
    testnet: "bg-blue-900/60 text-blue-100",
    mainnet: "bg-amber-900/70 text-amber-100",
    unknown: "bg-red-900/60 text-red-100",
};

function formatReason(reason) {
    if (!reason) return "Unknown readiness blocker";
    if (reasonLabels[reason]) return reasonLabels[reason];
    if (String(reason).startsWith("missing_binance_keys:")) {
        return `Binance API keys missing for ${String(reason).split(":").slice(1).join(":")}`;
    }
    return String(reason).replaceAll("_", " ");
}

function isFreshTimestamp(timestamp) {
    if (typeof timestamp !== "string" || !timestamp.trim()) return false;
    const parsed = Date.parse(timestamp);
    if (!Number.isFinite(parsed)) return false;
    const ageMs = Date.now() - parsed;
    return ageMs >= -READINESS_FUTURE_SKEW_MS && ageMs <= READINESS_MAX_AGE_MS;
}

function collectReadinessAliases(readiness) {
    return ["ready", "ok", "status"]
        .filter((field) => typeof readiness?.[field] === "boolean")
        .map((field) => ({ field, value: readiness[field] }));
}

function readinessAliasesConflict(aliases) {
    if (aliases.length <= 1) return false;
    return aliases.some((alias) => alias.value !== aliases[0].value);
}

function normalizeReadiness(readiness) {
    if (!readiness || typeof readiness !== "object" || Array.isArray(readiness)) {
        return {
            valid: false,
            unavailableReason: "Live status checks unavailable.",
            blockers: ["readiness_payload_missing"],
        };
    }

    const blockers = Array.isArray(readiness.blocking_reasons)
        ? readiness.blocking_reasons.filter(Boolean)
        : Array.isArray(readiness.reasons)
          ? readiness.reasons.filter(Boolean)
          : null;
    if (!Array.isArray(blockers)) {
        return {
            valid: false,
            unavailableReason: "Live status checks malformed.",
            blockers: ["readiness_payload_malformed"],
        };
    }

    const readinessAliases = collectReadinessAliases(readiness);
    const hasExplicitReady = readinessAliases.length > 0;
    const hasLiveRequested = typeof readiness.live_requested === "boolean";
    const hasExecutionMode = typeof readiness.effective_execution_mode === "string" || typeof readiness.execution_mode === "string";
    const hasFreshTimestamp = isFreshTimestamp(readiness.timestamp);
    if (!hasExplicitReady || !hasLiveRequested || !hasExecutionMode || !hasFreshTimestamp) {
        return {
            valid: false,
            unavailableReason: hasFreshTimestamp ? "Live status check fields missing." : "Live status checks stale or missing timestamp.",
            blockers: [hasFreshTimestamp ? "readiness_payload_incomplete" : "readiness_payload_stale", ...blockers],
        };
    }

    if (readinessAliasesConflict(readinessAliases)) {
        return {
            valid: false,
            unavailableReason: "Live status check fields conflict.",
            blockers: ["readiness_alias_conflict", ...blockers],
        };
    }

    return {
        valid: true,
        blockers,
        ready: readinessAliases.every((alias) => alias.value === true),
    };
}

export default function LiveModeStatusPanel({ readiness, binanceRules, className = "" }) {
    const normalized = normalizeReadiness(readiness);
    const env = normalized.valid ? readiness?.binance_env || "paper" : "unknown";
    const blocking = normalized.blockers || [];
    const effective = normalized.valid ? readiness?.effective_execution_mode || readiness?.execution_mode || "unknown" : "unknown";
    const liveRequested = normalized.valid ? readiness?.live_requested : false;
    const liveReady = Boolean(normalized.valid && normalized.ready && liveRequested && blocking.length === 0);

    const blockingText = blocking.map(formatReason).join(", ");
    const binanceEnvClass = envBadges[env] || envBadges.unknown;

    const rulesLoaded = binanceRules?.rules_loaded === true;
    const rulesMissing = Number.isFinite(Number(binanceRules?.symbols_missing_rules)) ? Number(binanceRules?.symbols_missing_rules) : 0;
    const rulesWith = Number.isFinite(Number(binanceRules?.symbols_with_rules)) ? Number(binanceRules?.symbols_with_rules) : 0;
    const totalLiveEnabled = rulesMissing + rulesWith;
    const missingCoins = Array.isArray(binanceRules?.missing_for_live_enabled) ? binanceRules.missing_for_live_enabled : [];

    return (
        <div className={`rounded-xl border border-gray-800 bg-gray-900/70 p-4 ${className}`} data-testid="live-mode-status">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <p className="text-[0.7rem] uppercase tracking-wide text-gray-400">Execution Mode</p>
                    <h3 className="text-lg font-semibold text-white">
                        {effective === "live" ? "Live" : effective === "paper" ? "Paper" : effective === "dry" || effective === "dry_run" ? "Dry" : "Unknown"}
                    </h3>
                    <p className="text-xs text-gray-400">Requested: {liveRequested ? "Live" : "Paper/Dry or unknown"}</p>
                </div>
                <div className="flex flex-col items-end gap-1 text-xs">
                    <span className={`px-3 py-1 rounded-full ${binanceEnvClass}`}>Binance Environment: {env.charAt(0).toUpperCase() + env.slice(1)}</span>
                    {normalized.valid && readiness?.any_binance_enabled_coins === true ? (
                        <span className="text-green-300">Binance-enabled coins configured</span>
                    ) : normalized.valid && readiness?.any_binance_enabled_coins === false ? (
                        <span className="text-amber-300">No Binance-enabled coins</span>
                    ) : (
                        <span className="text-amber-300">Binance coin readiness unknown</span>
                    )}
                </div>
            </div>

            <div className="mt-3 space-y-2 text-sm">
                {!normalized.valid ? (
                    <div className="text-amber-200" data-testid="live-unavailable">
                        Live checks: blocked — {normalized.unavailableReason} The dashboard is intentionally fail-closed.
                    </div>
                ) : liveReady ? (
                    <div className="text-green-200" data-testid="live-armed">
                        Live checks: passing. Live blockers: none reported; live mode remains API-gated.
                    </div>
                ) : blocking.length > 0 ? (
                    <div className="text-amber-200" data-testid="live-blocked">
                        Live checks: blocked — {blockingText || "pending checks"}.
                    </div>
                ) : (
                    <div className="text-amber-200" data-testid="live-disabled">
                        Live checks: blocked — live mode is disabled or not requested.
                    </div>
                )}

                {effective === "live" && !liveReady && (
                    <div className="text-amber-300" data-testid="live-misconfigured">
                        Live mode requested, but execution is blocked/fail-closed until status blockers are cleared. Paper results remain separate simulated data.
                    </div>
                )}

                <div className="mt-3 rounded-lg border border-gray-800 bg-gray-950/60 p-3" data-testid="binance-rules-box">
                    <div className="flex items-center justify-between text-xs text-gray-300">
                        <span>Binance Rules</span>
                        <span className={rulesLoaded && rulesMissing === 0 ? "text-green-300" : "text-amber-300"}>
                            {rulesLoaded ? "Loaded" : "Missing/Unknown"}
                        </span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-[0.8rem] text-gray-200">
                        <div>
                            <div className="text-gray-500 text-xs">Live-enabled coins</div>
                            <div>{totalLiveEnabled}</div>
                        </div>
                        <div>
                            <div className="text-gray-500 text-xs">With rules</div>
                            <div>{rulesWith}</div>
                        </div>
                        <div>
                            <div className="text-gray-500 text-xs">Missing rules</div>
                            <div className={rulesMissing > 0 ? "text-amber-300" : "text-gray-200"}>{rulesMissing}</div>
                        </div>
                    </div>
                    {missingCoins.length > 0 && (
                        <div className="mt-2 text-xs text-amber-200" data-testid="binance-rules-warning">
                            Missing rule entries for: {missingCoins.slice(0, 5).join(", ")}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

LiveModeStatusPanel.propTypes = {
    readiness: PropTypes.object,
    binanceRules: PropTypes.object,
    className: PropTypes.string,
};
